import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import GlobalStyles from '@mui/material/GlobalStyles';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { useState } from 'react';
import { Link as RouterLink, Outlet } from 'react-router';

import { Logo } from '@/components/Logo';
import { ThemeModeToggle } from '@/components/ThemeModeToggle';
import { useHashScroll } from '@/lib/use-hash-scroll';
import { ownProducts } from '@/site/cases';
import { varAlpha } from '@/theme';

import { WhatsAppFab } from '@/site/components/WhatsAppFab';
import { contactMailto, siteConfig, whatsappLink } from '@/site/site-config';

const HEADER_HEIGHT = 72;

export function SiteLayout() {
  useHashScroll();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Rolagem suave até as âncoras do menu, parando abaixo do cabeçalho fixo. */}
      <GlobalStyles
        styles={{
          html: { scrollBehavior: 'smooth', scrollPaddingTop: HEADER_HEIGHT + 16 },
          '@media (prefers-reduced-motion: reduce)': { html: { scrollBehavior: 'auto' } },
        }}
      />
      <SkipLink />
      <Header />
      <Box component="main" id="conteudo" tabIndex={-1} sx={{ flexGrow: 1, outline: 'none' }}>
        <Outlet />
      </Box>
      <Footer />
      <WhatsAppFab />
    </Box>
  );
}

/** Invisível até receber foco: quem navega por teclado pula o menu com um Tab. */
function SkipLink() {
  return (
    <Link
      href="#conteudo"
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: (theme) => theme.zIndex.tooltip,
        px: 2,
        py: 1,
        borderRadius: 1,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: 700,
        transform: 'translateY(-200%)',
        '&:focus-visible': { transform: 'none' },
      }}
    >
      Ir para o conteúdo
    </Link>
  );
}

function Header() {
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 24 });
  const [open, setOpen] = useState(false);
  const whatsapp = whatsappLink();

  return (
    <Box
      component="header"
      sx={(theme) => ({
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        height: HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        transition: 'background-color 200ms ease, box-shadow 200ms ease',
        bgcolor: scrolled
          ? varAlpha(theme.vars.palette.background.defaultChannel, 0.82)
          : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        boxShadow: scrolled ? theme.vars.customShadows.z4 : 'none',
      })}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Logo height={34} />

        <Stack
          component="nav"
          aria-label="Seções do site"
          direction="row"
          spacing={0.5}
          // Cinco itens mais o tema e o botão só cabem a partir de `lg`; abaixo disso, o menu.
          sx={{ display: { xs: 'none', lg: 'flex' }, ml: 4 }}
        >
          {siteConfig.nav.map((item) => (
            <Button
              key={item.href}
              component={RouterLink}
              to={item.href}
              color="inherit"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <ThemeModeToggle />

        <Button
          href={contactMailto()}
          variant="contained"
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, ml: 1 }}
        >
          Fale com a gente
        </Button>

        <IconButton
          aria-label="Abrir menu"
          onClick={() => setOpen(true)}
          color="inherit"
          sx={{ display: { lg: 'none' }, ml: 0.5 }}
        >
          <MenuRoundedIcon />
        </IconButton>
      </Container>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx: { width: 300, p: 3, bgcolor: 'background.default' } } }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', mb: 3 }}>
          <Logo height={30} />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton aria-label="Fechar menu" onClick={() => setOpen(false)}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Stack component="nav" aria-label="Seções do site (menu)" spacing={0.5}>
          {siteConfig.nav.map((item) => (
            <Button
              key={item.href}
              component={RouterLink}
              to={item.href}
              onClick={() => setOpen(false)}
              color="inherit"
              size="large"
              sx={{ justifyContent: 'flex-start', fontWeight: 600 }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
        <Stack spacing={1.5} sx={{ mt: 'auto' }}>
          {whatsapp && (
            <Button
              href={whatsapp}
              target="_blank"
              rel="noopener"
              variant="outlined"
              color="inherit"
              size="large"
              startIcon={<WhatsAppIcon />}
            >
              Chamar no WhatsApp
            </Button>
          )}
          <Button
            href={contactMailto()}
            variant="contained"
            size="large"
            startIcon={<MailOutlineRoundedIcon />}
          >
            Fale com a gente
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}

function Footer() {
  const whatsapp = whatsappLink();

  const columns = [
    { title: 'Navegação', links: siteConfig.nav.map((item) => ({ ...item, external: false })) },
    {
      title: 'Produtos',
      links: ownProducts
        .filter((product) => product.url)
        .map((product) => ({ label: product.name, href: product.url ?? '/', external: true })),
    },
    {
      title: 'Legal',
      links: [
        { label: 'Política de privacidade', href: '/politica-de-privacidade', external: false },
      ],
    },
    {
      title: 'Contato',
      links: [
        { label: siteConfig.contactEmail, href: contactMailto(), external: true },
        ...(whatsapp
          ? [{ label: siteConfig.whatsappDisplay, href: whatsapp, external: true }]
          : []),
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        borderTop: (theme) => `1px dashed ${varAlpha(theme.vars.palette.grey['500Channel'], 0.24)}`,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={{ xs: 5, md: 4 }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Logo height={34} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, maxWidth: 320 }}>
              Sistemas, aplicativos e integrações sob medida — do diagnóstico ao suporte, com um
              time que entende de produto e de nuvem.
            </Typography>
          </Grid>
          {columns.map((column) => (
            <Grid
              key={column.title}
              // O e-mail não quebra: a coluna de contato ganha a largura toda no celular.
              size={column.title === 'Contato' ? { xs: 12, sm: 3, md: 3 } : { xs: 6, sm: 3, md: 2 }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {column.title}
              </Typography>
              <Stack spacing={1.25}>
                {column.links.map((link) =>
                  link.external ? (
                    <Link
                      key={link.href}
                      href={link.href}
                      target={link.href.startsWith('http') ? '_blank' : undefined}
                      rel={link.href.startsWith('http') ? 'noopener' : undefined}
                      variant="body2"
                      color="text.secondary"
                      sx={{ '&:hover': { color: 'text.primary' }, whiteSpace: 'nowrap' }}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <Link
                      key={link.href}
                      component={RouterLink}
                      to={link.href}
                      variant="body2"
                      color="text.secondary"
                      sx={{ '&:hover': { color: 'text.primary' } }}
                    >
                      {link.label}
                    </Link>
                  ),
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 5, borderStyle: 'dashed' }} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ justifyContent: 'space-between', color: 'text.disabled' }}
        >
          <Typography variant="caption">
            © {new Date().getFullYear()} {siteConfig.company.legalName} · CNPJ{' '}
            {siteConfig.company.cnpj}
          </Typography>
          <Typography variant="caption">Todos os direitos reservados.</Typography>
        </Stack>
      </Container>
    </Box>
  );
}
